import classnames from 'classnames'
import React, { memo, useCallback } from 'react'

import { ProductTags } from '@src/components/product-card/product-image-section/product-tags'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import Directory from '@src/quince-site-common/src/domains/Directory'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import usePDPTagVisibility from '@src/quince-site-common/src/hooks/usePDPTagVisibility'
import { TAG_POSITION } from '@src/quince-site-common/src/utils/constants'
import { useShowNewPdpUI } from '@src/quince-site-pdp/src/templates/productPage/hooks/useShowNewPdpUI'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'

import Heart from '../../../../quince-site-common/src/organisms/appLayout/molecules/header/icons/heart.inline.svg'
import { BreadCrumb } from '../breadCrumb'
import Icon from '../icon/Icon'
import { SliderImages, SliderImagesProps } from '../sliderImages'

import styles from './sliderImageText.module.scss'

export interface SliderImageWithTextProps {
  className?: string
  sliderImageProps: SliderImagesProps
  isWishListed?: boolean
  hideWishList?: boolean
  productTags?: ProductTag[]
  onWishListToggle?: (e: React.MouseEvent<HTMLElement | SVGElement>) => void
  directory: Directory
  getTopLeftIconSVG?: ({ index }: { index: number }) => ((params: any) => JSX.Element) | null
  hideBreadCrumb?: boolean
  breadCrumbListClass?: string
  breadCrumbContainerHeaderClass?: string
  variants?: any[]
  product?: Product
}

const SliderImageWithText = ({
  className,
  sliderImageProps,
  isWishListed,
  onWishListToggle,
  productTags,
  directory,
  hideWishList,
  getTopLeftIconSVG,
  hideBreadCrumb,
  breadCrumbListClass,
  breadCrumbContainerHeaderClass,
  variants,
  product,
}: SliderImageWithTextProps) => {
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const classes = classnames(styles.container, className)
  const { isTagVisible } = usePDPTagVisibility()
  const STYLE_WHITE = 'white'
  const STYLE_BLACK = 'black'
  const innerStyle = {
    fill: isWishListed ? STYLE_BLACK : STYLE_WHITE,
  }
  const showNewPdpUI = useShowNewPdpUI()
  const renderMainImageOverlay = useCallback(
    (index: number) => {
      if (!productTags) return null
      const topLeftIconSVG = getTopLeftIconSVG?.({ index })
      return (
        <div className={styles.imageOverlay}>
          {topLeftIconSVG ? (
            <div className={styles.awardIcon}>
              <Icon iconSvg={topLeftIconSVG} className={styles.iconStyles} />
            </div>
          ) : null}
          {isTagVisible && (
            <ProductTags applicableTags={productTags} position={isMobile ? TAG_POSITION.BOTTOM : TAG_POSITION.TOP} />
          )}
        </div>
      )
    },
    [productTags, getTopLeftIconSVG, isMobile, isTagVisible],
  )

  const videoTrackingProperties = {
    productProperties: product ? getProductPropertiesForTracking(product) : undefined,
  }

  return (
    <div className={classes}>
      <div
        className={classnames(
          styles.container__header,
          breadCrumbContainerHeaderClass,
          showNewPdpUI && styles.container__header_adjustedPadding,
        )}
      >
        {!hideBreadCrumb && (
          <div className={styles.container__header__text}>
            <BreadCrumb directory={directory} breadCrumbListClass={breadCrumbListClass} />
          </div>
        )}
        {!hideWishList && (
          <Heart
            data-testid={
              isWishListed ? TEST_LOCATORS.REMOVE_FROM_WISHLIST_BUTTON : TEST_LOCATORS.ADD_TO_WISHLIST_BUTTON
            }
            style={innerStyle}
            className={styles.container__header__icon}
            onClick={onWishListToggle}
          />
        )}
      </div>
      <SliderImages
        {...sliderImageProps}
        renderMainImageOverlay={renderMainImageOverlay}
        variants={variants}
        product={product}
        videoTrackingProperties={videoTrackingProperties}
      />
    </div>
  )
}

export default memo(SliderImageWithText)
